/**
 * PGSS-5384 / PGSS-5409 / PGSS-5546 — which staff lists use the new page, and how each one labels itself.
 * Lives under src/app/main/apps/staff. Customer / supplier portals stay on the existing page.
 */

import { authRoles } from 'app/auth';

export const STAFF_LIST_TYPES = ['order', 'quote', 'rfq', 'invoice', 'container'];

export function usesStaffList(type) {
  return STAFF_LIST_TYPES.includes(type);
}

function roleList(user) {
  const role = user?.role;
  if (Array.isArray(role)) {
    return role.filter(Boolean);
  }
  return role ? [role] : [];
}

/**
 * Allowlist: only roles already named as staff (admin / staff / sales).
 * Anyone else — customer, supplier, VAS, warehouse, or a role added later —
 * keeps the existing Orders page until someone opts them in here.
 */
export function shouldUseStaffList(user, type) {
  if (!usesStaffList(type)) {
    return false;
  }
  return roleList(user).some((role) => authRoles.staff.includes(role));
}

/**
 * @param {string} type
 * @param {{ for?: string }} [options] — `for` distinguishes customer vs supplier invoice lists
 */
export function getStaffListConfig(type, options = {}) {
  const isSupplierInvoice = options.for === 'supplier-invoices';
  switch (type) {
    case 'quote':
      return {
        title: 'Quotes',
        noun: 'quotes',
        newLabel: 'New quote',
        newLink: '/quotes/new',
        importName: 'quote',
        importTitle: 'Import Multiple Quotes',
        exportTitle: 'Export quotes',
        statusCategory: 'quote_status',
        listLabel: 'Quotes',
        showCreate: true,
      };
    case 'rfq':
      return {
        title: 'RFQs',
        noun: 'RFQs',
        newLabel: 'New RFQ',
        newLink: '/rfqs/request/new?type=request',
        importName: 'rfq',
        importTitle: 'Import Multiple RFQs',
        exportTitle: 'Export RFQs',
        statusCategory: 'rfq_status',
        listLabel: 'Rfqs',
        showCreate: true,
      };
    case 'invoice':
      return {
        title: isSupplierInvoice ? 'Supplier invoices' : 'Invoices',
        noun: isSupplierInvoice ? 'supplier invoices' : 'invoices',
        newLabel: null,
        newLink: null,
        importName: null,
        importTitle: null,
        exportTitle: isSupplierInvoice ? 'Export supplier invoices' : 'Export invoices',
        statusCategory: 'invoice_status',
        listLabel: isSupplierInvoice ? 'Supplier invoices' : 'Invoices',
        showCreate: false,
      };
    case 'container':
      return {
        title: 'Container Shipments',
        noun: 'container shipments',
        newLabel: null,
        newLink: null,
        importName: null,
        importTitle: null,
        exportTitle: null,
        // Overridden by the active tab: staffListContainerStatusCategory(viewType).
        statusCategory: 'container-awaiting-approval_status',
        listLabel: 'Container Shipments',
        showCreate: false,
      };
    default:
      return {
        title: 'Orders',
        noun: 'orders',
        newLabel: 'New order',
        newLink: '/orders/new',
        importName: 'order',
        importTitle: 'Import Multiple Orders',
        exportTitle: 'Export orders',
        statusCategory: 'order_status',
        listLabel: 'Orders',
        showCreate: true,
      };
  }
}
